/**
 * useWeblabTrackerData — data fetching hook for the Weblab Tracker plugin.
 *
 * Reads the hourly Athena snapshot of the Weblab Tracker view page from
 * `weblab_tableau_prod.weblab_metrics_data_prod` (catalog account
 * 422237711394), via the dedicated `getWeblabView` GraphQL query backed by
 * the existing `weblab_athena_client.py` Lambda.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY A DEDICATED QUERY (not asyncAthenaQuery / getASFMPreProcessedData)
 * ───────────────────────────────────────────────────────────────────────────
 * The async_athena_query and asfm_preprocessed_data Lambdas both accept a
 * `database` argument, but they build their SQL exclusively through
 * query_store.get_query() with a fixed set of ASFM query names
 * (trend_query, preprocessed_perf_query, forecast_traffic_query,
 * latest_dataset_date_query). There is no path to pass a raw weblab SELECT.
 *
 * weblab_athena_client.py, by contrast, already accepts a raw `query`,
 * already points at `weblab_tableau_prod`, and already assumes
 * finn-asfm-read-access-lambda-role. So the weblab read path stays isolated
 * from the ASFM one and needs only a thin new resolver.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * ACCESS MODEL
 * ───────────────────────────────────────────────────────────────────────────
 * All teams are visible by default. The filter panel lets the user pick a
 * single team (filters.teamName); when set, we add a `team_name = '...'`
 * WHERE clause. When unset, all teams are returned.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * DATE RANGE FILTER (server-side)
 * ───────────────────────────────────────────────────────────────────────────
 * filters.dateRange controls how much data Athena returns:
 *   "currentMonth" → rows where any relevant date matches YYYY-MM (default)
 *   "currentYear"  → rows where any relevant date matches YYYY
 *   "all"          → no date filtering (full partition)
 *
 * This is pushed into the SQL WHERE clause to reduce Athena scan output,
 * eliminate unnecessary pagination, and cut response payload size.
 *
 * Returns PluginDataResult { data, isLoading, error, refetch }.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { PluginDataResult } from "../../types";
import { fetchWeblabView, extractGraphQLError } from "./weblabTrackerService";
import type { WeblabRow } from "./weblabColumns";

const WEBLAB_DATABASE = "weblab_tableau_prod";
const WEBLAB_TABLE = "weblab_metrics_data_prod";
const PAGE_SIZE = 800;
const MAX_PAGES = 25; // Safety cap: 800 × 25 = 20000 rows max

/** Shape of the data returned by this hook. */
export interface WeblabTrackerData {
  rows: WeblabRow[];
  /** run_date partition the rows were read from (for a "data as of" label). */
  runDate: string | null;
  rowCount: number;
}

export type DateRangeFilter = "all" | "currentYear" | "currentMonth";

/** Escape a single-quoted SQL string literal. */
function sqlLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * Build the date-range needle for SQL LIKE clauses.
 *
 * IMPORTANT: Date columns in weblab_metrics_data_prod are stored in
 * M/D/YYYY format (e.g. "7/15/2026", "12/1/2025"), NOT ISO format.
 * The year is at the END of the string.
 *
 * Client-side uses .includes("2026") which matches anywhere in the string
 * (including at the end). SQL must use suffix/contains match accordingly.
 *
 *   "currentMonth" → "%/2026" for year + "7/%" for month prefix
 *   "currentYear"  → "%/2026" (suffix match — year at end)
 *   "all"          → null (no filtering)
 */
function getDateNeedles(dateRange: DateRangeFilter): {
  yearSuffix: string;
  monthPrefix: string | null;
} | null {
  if (dateRange === "all") return null;
  const now = new Date();
  const year = String(now.getFullYear());
  const yearSuffix = `%/${year}`; // matches "7/15/2026", "12/1/2026", etc.
  if (dateRange === "currentMonth") {
    const month = String(now.getMonth() + 1); // NOT zero-padded (M/D/YYYY uses 7 not 07)
    return { yearSuffix, monthPrefix: `${month}/%/${year}` };
  }
  return { yearSuffix, monthPrefix: null }; // currentYear — match year only
}

/**
 * Build the shared WHERE clause (partition + team + date filter).
 *
 * Reused by both the count query and the paged data queries so the two
 * always agree on which rows they cover.
 *
 * Filters:
 *   - latest run_date partition (freshest hourly snapshot)
 *   - team_name (when a single team is selected)
 *   - date range (currentMonth/currentYear → LIKE on date columns)
 */
function buildWhereClause(
  teamName: string | null,
  dateRange: DateRangeFilter
): string {
  const teamFilter = teamName ? `AND team_name = ${sqlLiteral(teamName)}` : "";

  // Date range filter: dates are M/D/YYYY format (year at end).
  // "all" → null → no date filter in SQL.
  const needles = getDateNeedles(dateRange);
  let dateFilter = "";
  if (needles) {
    const { yearSuffix, monthPrefix } = needles;
    // currentMonth: '7/%/2026' matches "7/1/2026", "7/15/2026", "7/31/2026"
    // currentYear:  '%/2026' matches "7/15/2026", "12/1/2026", etc.
    const dateLike = monthPrefix
      ? sqlLiteral(monthPrefix)
      : sqlLiteral(yearSuffix);

    dateFilter = `
    AND (
      actual_launch_date LIKE ${dateLike}
      OR expected_launch_date LIKE ${dateLike}
      OR experiment_start_date LIKE ${dateLike}
      OR (
        (actual_launch_date IS NULL OR actual_launch_date = '')
        AND (expected_launch_date IS NULL OR expected_launch_date = '')
        AND (experiment_start_date IS NULL OR experiment_start_date = '')
      )
    )`;
  }

  return `
    WHERE run_date = (SELECT max(run_date) FROM ${WEBLAB_TABLE})
    ${teamFilter}
    ${dateFilter}
  `.trim();
}

/**
 * Count how many rows match the filter — used to plan parallel pagination.
 */
function buildCountQuery(
  teamName: string | null,
  dateRange: DateRangeFilter
): string {
  return `
    SELECT count(*) AS n
    FROM ${WEBLAB_TABLE}
    ${buildWhereClause(teamName, dateRange)}
  `.trim();
}

/**
 * Build one page of the result using OFFSET-based pagination.
 *
 * Unlike cursor pagination (`AND id < lastId`), offset pagination does NOT
 * depend on the previous page's result, so all pages can be fetched in
 * parallel. `ORDER BY id DESC` gives a stable ordering across pages.
 */
function buildPageQuery(
  teamName: string | null,
  dateRange: DateRangeFilter,
  offset: number
): string {
  return `
    SELECT *
    FROM ${WEBLAB_TABLE}
    ${buildWhereClause(teamName, dateRange)}
    ORDER BY id DESC
    OFFSET ${offset}
    LIMIT ${PAGE_SIZE}
  `.trim();
}

/**
 * Custom hook implementing the Weblab Tracker data fetching flow.
 *
 * PARALLEL PAGINATION
 * ───────────────────
 * The previous implementation paged sequentially with a cursor
 * (`AND id < lastId`): each page had to wait for the previous page's last
 * id, so N pages meant N serial round-trips — the dominant bottleneck.
 * Now we count first, compute offsets up front, and fire all page queries
 * concurrently with Promise.all. Total latency ≈ one COUNT round-trip +
 * one page round-trip (pages overlap), not N serial round-trips.
 *
 * @param filters - may contain:
 *   - `teamName: string` to scope to one team ("" = all teams)
 *   - `dateRange: DateRangeFilter` to scope by date ("currentMonth" default)
 */
export function useWeblabTrackerData(
  filters: Record<string, any>
): PluginDataResult {
  const [data, setData] = useState<WeblabTrackerData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const teamName: string | null = filters?.teamName ?? null;
  const dateRange: DateRangeFilter = filters?.dateRange ?? "currentMonth";

  // Skip fetching until the content component initializes the team filter.
  // teamName semantics:
  //   null  → filter not initialized yet (content mounts and immediately
  //           sets it), so hold off to avoid a duplicate initial request
  //   ""    → "All Teams": fetch every team (no team_name WHERE clause);
  //           pagination keeps each response under the Lambda 6MB cap
  //   "X"   → single team: scope the SQL to team_name = 'X'
  const ready = teamName != null;

  const fetchData = useCallback(async () => {
    if (!ready) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: count matching rows so we can plan pagination.
      const countResult = await fetchWeblabView({
        query: buildCountQuery(teamName, dateRange),
        database: WEBLAB_DATABASE,
        max_results: 1,
      });
      if (controller.signal.aborted) return;

      const totalRows = Number(countResult?.rows?.[0]?.n ?? 0);

      // Step 2: compute page offsets (0, PAGE_SIZE, 2*PAGE_SIZE, …),
      // capped at MAX_PAGES for safety.
      const pageCount = Math.min(Math.ceil(totalRows / PAGE_SIZE), MAX_PAGES);
      const offsets = Array.from(
        { length: pageCount },
        (_, i) => i * PAGE_SIZE
      );

      // Step 3: fetch all pages CONCURRENTLY.
      const pages = await Promise.all(
        offsets.map((offset) =>
          fetchWeblabView({
            query: buildPageQuery(teamName, dateRange, offset),
            database: WEBLAB_DATABASE,
            max_results: PAGE_SIZE,
          })
        )
      );
      if (controller.signal.aborted) return;

      // Step 4: concatenate pages in offset order (offsets array is ordered).
      const allRows: WeblabRow[] = [];
      for (const page of pages) {
        const pageRows: WeblabRow[] = Array.isArray(page?.rows)
          ? (page.rows as WeblabRow[])
          : [];
        allRows.push(...pageRows);
      }

      const runDate =
        allRows.length > 0 ? (allRows[0].run_date as string) ?? null : null;

      setData({ rows: allRows, runDate, rowCount: allRows.length });
    } catch (err: any) {
      if (controller.signal.aborted) return;
      console.error("useWeblabTrackerData fetch failed:", err);
      setError(
        extractGraphQLError(err) || "Failed to load Weblab Tracker data"
      );
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [teamName, dateRange, ready]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  return {
    data,
    isLoading: isLoading || !ready,
    error,
    refetch: fetchData,
  };
}
